import type L from "leaflet";
import type { Cluster } from "./clustering";

/** 마커 반지름(px). 아이콘 크기의 절반. */
const CLUSTER_R = 19;
const PIN_R = 15;
/** 서로 이만큼은 떨어져야 둘 다 읽힌다. */
const GAP = 8;

/**
 * 겹치는 클러스터를 화면 좌표에서 밀어낸다.
 *
 * 지역 중심을 잡아줘도 해결되지 않는 경우가 있다 — **경기·인천은 서울을 둘러싸고 있어서**
 * 어느 지점을 대표로 잡아도 서울 마커 옆에 선다. 지리적으로 풀 수 없으니 화면에서 푼다.
 *
 * 움직이는 건 **클러스터뿐**이다. 단독 핀은 한 현장의 실제 위치를 주장하므로 옮기면
 * 거짓말이 된다. 반면 클러스터는 이미 "이 권역"이라는 뭉뚱그린 표시라, 조금 밀려도
 * 뜻이 달라지지 않는다.
 */
export function declutterClusters(map: L.Map, clusters: Cluster[]): Cluster[] {
  const pts = clusters.map((c) => ({
    c,
    p: map.latLngToLayerPoint([c.lat, c.lon]),
    r: c.items.length > 1 ? CLUSTER_R : PIN_R,
    movable: c.items.length > 1,
  }));

  // 두세 번이면 수렴한다 — 전국 현장은 많아야 수십 개다.
  for (let pass = 0; pass < 4; pass += 1) {
    let moved = false;
    for (let i = 0; i < pts.length; i += 1) {
      for (let j = i + 1; j < pts.length; j += 1) {
        const a = pts[i]!;
        const b = pts[j]!;
        if (!a.movable && !b.movable) continue;

        const dx = b.p.x - a.p.x;
        const dy = b.p.y - a.p.y;
        const need = a.r + b.r + GAP;
        const dist = Math.hypot(dx, dy);
        if (dist >= need) continue;

        // 완전히 겹쳐 방향이 없으면 임의로 오른쪽으로 뗀다.
        const ux = dist > 0.01 ? dx / dist : 1;
        const uy = dist > 0.01 ? dy / dist : 0;
        const push = need - dist;

        // 고정된 쪽이 있으면 움직일 수 있는 쪽이 전부 감당한다.
        const aShare = a.movable ? (b.movable ? 0.5 : 1) : 0;
        const bShare = b.movable ? (a.movable ? 0.5 : 1) : 0;
        a.p = a.p.subtract([ux * push * aShare, uy * push * aShare]);
        b.p = b.p.add([ux * push * bShare, uy * push * bShare]);
        moved = true;
      }
    }
    if (!moved) break;
  }

  return pts.map(({ c, p, movable }) => {
    if (!movable) return c;
    const ll = map.layerPointToLatLng(p);
    return { ...c, lat: ll.lat, lon: ll.lng };
  });
}
